import React, { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { icons } from "../../../icons";
import { StartPage, Button, ModalVariables } from "../../../components";
import { useNavigate } from "react-router";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { volumeTypesService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import { useAuth } from "../../../context/user.context";
import { compareArrays } from "../../../utils/findIntersection";

const VolumeTypes: FC = () => {
  const {
    viewAlert,
  } = useUi()
  const { user, quotas } = useAuth()
  const [types, setTypes] = useState<any[]>([])
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering = '-created_at', setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [paginationData, setPaginationData] = useState<any>()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [selectedType, setSelectedType] = useState<any>()
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const navigate = useNavigate()

  const editChecked = (id: any, isAll?: boolean) => {
    if (id === null) {
      setChecked([])
      return
    }
    const founded = checked.find((item: any) => item.id === id?.id)
    if (founded) {
      if (isAll) {
        return
      } else {
        setChecked((prev: any) => {
          return prev.filter((item: any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  }

  useEffect(() => {
    if (compareArrays(types, checked) && checked?.length !== 0) {
      setAllChecked(true)
    }
  }, [checked])

  const [t] = useTranslation()

  const _updateTypes = () => {
    volumeTypesService.getTypeList({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: ordering,
      search: searchString || ''
    })
      .then((res) => {
        setTypes(res.data.objects.map((type: any) => {
          return {
            ...type,
            navigate: type.id,
            nameStr: type.name || type.id,
            name: type.name || type.id,
            created_at: type.created_at ? timeConverterMMDDYYHHMM(type.created_at) : 'N/A',
            is_public_display: type.is_public ? t("YES") : t("NO"),
          }
        }))
        setPaginationData(res.data)
        setPageLoading(false)
      })
      .catch((e) => {
        viewAlert({
          severity: 'error',
          message: e?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
        })
        setPageLoading(false)
      })
  }

  useEffect(() => {
    _updateTypes()
    const interval = setInterval(() => {
      _updateTypes()
    }, 10000)
    return () => {
      clearInterval(interval)
    }
  }, [page,
    ordering,
    pageSize,
    searchString])

  const openModal = (modal: any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }

  const closeModal = async (values: any) => {
    let res: any;
    try {
      setLoading(true);
      switch (modalType) {
        case "delete":
          try {
            if (allChecked) {
              await Promise.all(types.map((item) => volumeTypesService.deleteType(item.id)));
              _updateTypes();
              setAllChecked(false);
              setChecked([]);
            } else if (checked?.length > 0) {
              await Promise.all(checked.map((item) => volumeTypesService.deleteType(item.id)));
              _updateTypes();
              setChecked([]);
            } else if (selectedType) {
              await volumeTypesService.deleteType(selectedType.id);
              _updateTypes();
            }
          } catch (e: any) {
            if (e?.response?.data.detail) {
              viewAlert({
                severity: 'error',
                message: e.response.data.detail,
              });
            }
            setLoading(false);
          } finally {
            setViewModal(false);
            setActiveAnimation(false);
          }
          break;
      }
      setViewModal(false)
      setActiveAnimation(false)
    } catch (e: any) {
      if (e?.response?.data.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  const items = [
    {
      title: t("EDIT"),
      id: '2',
      icon: icons.Edit,
      onClick: (type: any) => {
        navigate(`/openstack/volume-types/${type.id}/edit`)
      },
      divider: false
    },
    {
      title: t("DELETE"),
      id: '4',
      onClick: (type: any) => {
        openModal('delete')
        setAlertTitle(t("VOLUME_TYPE"))
        setAlertSubTitle([type])
        setSelectedType(type)
      },
      icon: icons.DeleteIcon,
      divider: false
    },
  ]

  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }

  const Columns = [
    {
      title: t("NAME"),
      key: 'name'
    },
    {
      title: t("DESCRIPTION"),
      key: 'description'
    },
    {
      title: t("IS_PUBLIC"),
      key: 'is_public_display'
    },
    {
      title: t("REGION"),
      key: 'region'
    },
    {
      title: '',
      key: 'props'
    },
  ]

  return (
    <>
      <StartPage
        title={`${t("VOLUME_TYPES")}`}
        noResourceText={`${t("NO_VOLUME_TYPES")}`}
        createResourceButton={`${t('CREATE_VOLUME_TYPE')}`}
        createResourceButtonEventOrLink={() => navigate('/openstack/volume-types/create')}
        haveResource={types && types.length > 0}
        rightButtonComponentCustomWithResources={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={() => {
                setAlertTitle(t("VOLUME_TYPES"))
                setAlertSubTitle(allChecked ? types : checked)
                openModal('delete')
              }}
              variant='icon'
              size='display'
              test_id='button-delete'
              disabled={!allChecked && checked?.length === 0}
              customStyles={{ margin: '0px 6px 0px 0px' }}
            >
              <img src={icons.DeleteIcon} />
            </Button>
          </div>
        }
        pageLoading={pageLoading}
        tableData={{
          columns: Columns,
          data: types,
          props: items,
          allChecked: allChecked,
          setAllChecked: setAllChecked,
          checked: checked,
          setChecked: editChecked,
          paginationData: paginationData,
          updateData: _updateTypes
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
      />
    </>
  )
}

export default VolumeTypes;
